import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  Select,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import ReactECharts from "echarts-for-react";
import CommonAccordion from "../../custom-components/CommonAccordion";
import { ReactComponent as ExportIcon } from "../../../images/icon.svg";
// import { ReactComponent as GreenIcon } from "../../../images/GreenCircleIcon.svg";
// import { ReactComponent as ErrorMediumIcon } from "../../../images/ErrorMedium.svg";
// import { ReactComponent as SafeAlertLineIcon } from "../../../images/SafeAlertLine.svg";
import CommonDownload from "../../custom-components/CommonDownload";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { useNavigate } from "react-router-dom";
import {
  downloadNodeAsHtml,
  makeTimestampedName,
} from "../../common-components/DownloadHTML";

const SASTScanDashboard = ({
  runType,
  sastScanResult, // Now contains already-grouped enhanced OWASP data from backend
  scanDate,
  codeSummary,
}) => {
  const dashboardRef = useRef(null);
  const navigate = useNavigate();
  const [scanResults, setScanResults] = useState(sastScanResult || []);
  const [dateTime, setDateTime] = useState(scanDate || "");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  const [lowSeverity, setLowSeverity] = React.useState(0);
  const [mediumSeverity, setMediumSeverity] = React.useState(0);
  const [highSeverity, setHighSeverity] = React.useState(0);
  const [criticalSeverity, setCriticalSeverity] = React.useState(0);
  const [sortSeverity, setSortSeverity] = useState("All");
  const [openDownload, setOpenDownload] = useState(false);
  const [languageFilter, setLanguageFilter] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  function handleDownloadHTML() {
    const filename = makeTimestampedName("SAST-Dashboard", "html");
    downloadNodeAsHtml(dashboardRef, {
      filename,
      selfContained: true,
      includeExternalCssLinks: false,
      captureCharts: true, // This is now the default
    });
  }

  // Enhanced HTML Download with better rendering
  // const handleDownloadHTML = async () => {
  //   if (!dashboardRef.current) return;

  //   setIsDownloading(true);

  //   try {
  //     // Get the dashboard element
  //     const element = dashboardRef.current;

  //     // Prepare the element for capture
  //     await prepareElementForCapture(element);

  //     // Capture with optimized settings
  //     const canvas = await html2canvas(element, {
  //       useCORS: true,
  //       scale: 2, // Higher resolution
  //       backgroundColor: "#FFFFFF",
  //       scrollX: 0,
  //       scrollY: 0,
  //       width: element.scrollWidth,
  //       height: element.scrollHeight,
  //       windowWidth: window.innerWidth,
  //       windowHeight: window.innerHeight,
  //       allowTaint: false,
  //       foreignObjectRendering: true, // Better SVG support
  //       imageTimeout: 15000,
  //       removeContainer: true,
  //       logging: false,
  //       onclone: (clonedDoc) => {
  //         // Style the cloned document for better rendering
  //         const clonedElement = clonedDoc.querySelector("[data-dashboard-ref]");
  //         if (clonedElement) {
  //           clonedElement.style.transform = "none";
  //           clonedElement.style.width = `${element.scrollWidth}px`;
  //           clonedElement.style.height = "auto";
  //           clonedElement.style.overflow = "visible";

  //           // Fix Material-UI specific issues
  //           const muiElements = clonedDoc.querySelectorAll(
  //             ".MuiTableContainer-root"
  //           );
  //           muiElements.forEach((el) => {
  //             el.style.overflow = "visible";
  //             el.style.maxHeight = "none";
  //           });
  //         }
  //       },
  //     });

  //     // Convert to high-quality image
  //     const imgData = canvas.toDataURL("image/png", 1.0);

  //     // Create HTML with embedded image
  //     const htmlContent = createEnhancedHTML(imgData);

  //     // Download HTML
  //     const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  //     const filename = `SAST_Dashboard_${timestamp}.html`;

  //     const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
  //     saveAs(blob, filename);

  //     // Restore element
  //     await restoreElement(element);
  //   } catch (error) {
  //     console.error("HTML download failed:", error);
  //     alert("Failed to download HTML. Please try again.");
  //   } finally {
  //     setIsDownloading(false);
  //   }
  // };

  // Enhanced PDF Download with better quality

  const handleDownloadPDF = async () => {
    if (!dashboardRef.current) return;

    const downloadButton = document.querySelector("[data-pdf-download-button]");
    const originalText = downloadButton?.textContent || "PDF Download";
    const originalDisabled = downloadButton?.disabled || false;
    const originalBackgroundColor =
      downloadButton?.style.backgroundColor || "#49A3FF";

    try {
      if (downloadButton) {
        downloadButton.textContent = "Downloading...";
        downloadButton.disabled = true;
        downloadButton.style.backgroundColor = "#A5A5A5";
        downloadButton.style.cursor = "not-allowed";
      }

      // 🔥 ADAPTED FOR SAST DATA STRUCTURE
      const vulnerabilities = scanResults
        .filter((vuln) => vuln.Impact || vuln.impact) // Filter out any without severity
        .sort((a, b) => {
          const severityOrder = { Critical: 1, High: 2, Medium: 3, Low: 4 };
          const aSev = normalizeSeverity(a.Impact || a.impact);
          const bSev = normalizeSeverity(b.Impact || b.impact);
          return severityOrder[aSev] - severityOrder[bSev];
        });

      // Capture chart
      let chartImageData = null;
      const chartElement =
        dashboardRef.current.querySelector(".echarts-for-react");

      if (chartElement) {
        try {
          const chartCanvas = await html2canvas(chartElement, {
            useCORS: true,
            scale: 3,
            backgroundColor: "#FFFFFF",
            allowTaint: true,
            logging: false,
          });
          chartImageData = chartCanvas.toDataURL("image/png", 1.0);
        } catch (chartError) {
          console.warn("Could not capture chart for PDF:", chartError);
        }
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 15;
      const bottomMargin = 25;
      const availableWidth = pdfWidth - margin * 2;
      const safeBottomY = pdfHeight - bottomMargin;

      let currentY = 20;

      // Helper functions (same as DAST)
      const calculateTextHeight = (
        text,
        lineWidth = availableWidth - 10,
        fontSize = 9
      ) => {
        pdf.setFontSize(fontSize);
        const splitText = pdf.splitTextToSize(text, lineWidth);
        const lineHeight = fontSize * 0.35;
        return splitText.length * lineHeight + 10;
      };

      const checkPageBreak = (requiredHeight, addExtraMargin = 5) => {
        const availableHeight = safeBottomY - currentY - addExtraMargin;
        if (requiredHeight > availableHeight) {
          pdf.addPage();
          currentY = 25;
          return true;
        }
        return false;
      };

      // Header Section - ADAPTED FOR SAST
      pdf.setFontSize(20);
      pdf.setTextColor(25, 118, 210);
      pdf.text("SAST Security Analysis Dashboard", pdfWidth / 2, currentY, {
        align: "center",
      });
      currentY += 15;

      pdf.setFillColor(241, 248, 255);
      pdf.rect(margin, currentY, availableWidth, 25, "F");

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Last Scan: ${dateTime}`, margin + 5, currentY + 8);
      pdf.text(
        `Total Vulnerabilities: ${scanResults.length}`,
        margin + 5,
        currentY + 15
      );

      // Severity counts in header
      pdf.setTextColor(174, 36, 30);
      pdf.text(`Critical: ${criticalSeverity}`, margin + 5, currentY + 22);
      pdf.setTextColor(255, 64, 64);
      pdf.text(`High: ${highSeverity}`, margin + 45, currentY + 22);
      pdf.setTextColor(255, 165, 0);
      pdf.text(`Medium: ${mediumSeverity}`, margin + 80, currentY + 22);
      pdf.setTextColor(56, 142, 60);
      pdf.text(`Low: ${lowSeverity}`, margin + 120, currentY + 22);

      currentY += 40;

      // Check space before severity circles
      checkPageBreak(60);

      // Severity Circles - ADAPTED FOR SAST
      const circleData = [
        {
          label: "CRITICAL",
          count: criticalSeverity,
          color: [174, 36, 30],
          shadowColor: [174, 36, 30],
        },
        {
          label: "HIGH",
          count: highSeverity,
          color: [255, 64, 64],
          shadowColor: [255, 64, 64],
        },
        {
          label: "MEDIUM",
          count: mediumSeverity,
          color: [255, 165, 0],
          shadowColor: [255, 165, 0],
        },
        {
          label: "LOW",
          count: lowSeverity,
          color: [56, 142, 60],
          shadowColor: [56, 142, 60],
        },
      ];

      const circleSpacing = availableWidth / 4;
      const circleRadius = 20;

      circleData.forEach((item, index) => {
        const centerX = margin + circleSpacing * index + circleSpacing / 2;
        const centerY = currentY + circleRadius + 10;

        // Create crescent shadow effect
        pdf.setFillColor(...item.shadowColor);
        pdf.circle(centerX + 3, centerY, circleRadius, "F");

        // Main white circle
        pdf.setFillColor(255, 255, 255);
        pdf.circle(centerX, centerY, circleRadius, "F");

        // Circle border
        pdf.setDrawColor(240, 240, 240);
        pdf.setLineWidth(0.5);
        pdf.circle(centerX, centerY, circleRadius, "S");

        // Count text
        pdf.setFontSize(18);
        pdf.setTextColor(
          item.count === 0 ? 117 : item.color[0],
          item.count === 0 ? 117 : item.color[1],
          item.count === 0 ? 117 : item.color[2]
        );
        pdf.text(item.count.toString(), centerX, centerY - 2, {
          align: "center",
        });

        // Label text
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(item.label, centerX, centerY + 8, { align: "center" });
      });

      currentY += circleRadius * 2 + 30;

      // Chart Section (same as DAST)
      if (chartImageData) {
        checkPageBreak(90);

        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(margin, currentY, availableWidth, 85, 3, 3, "F");

        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text("Vulnerability Count By Severity", margin + 10, currentY + 12);

        const chartX = margin + 10;
        pdf.addImage(
          chartImageData,
          "PNG",
          chartX,
          currentY + 15,
          availableWidth - 20,
          65
        );
        currentY += 100;
      }

      // Summary Table - ADAPTED FOR SAST STRUCTURE
      checkPageBreak(50);

      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin, currentY, availableWidth, 20, 3, 3, "F");

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(
        "Vulnerabilities by Name and Severity",
        margin + 10,
        currentY + 12
      );
      currentY += 30;

      const tableData = vulnerabilities.map((vuln, index) => [
        (index + 1).toString(),
        vuln.vulnerability_name || "N/A",
        normalizeSeverity(vuln.Impact || vuln.impact),
      ]);

      if (tableData.length > 0) {
        pdf.autoTable({
          head: [["S.No", "Vulnerability Name", "Severity"]],
          body: tableData,
          startY: currentY,
          styles: {
            fontSize: 10,
            cellPadding: 4,
            lineColor: [221, 221, 221],
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [0, 133, 222],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 11,
          },
          alternateRowStyles: {
            fillColor: [240, 249, 255],
          },
          columnStyles: {
            0: { cellWidth: 15, halign: "center" },
            1: { cellWidth: 130 },
            2: { cellWidth: 35, halign: "center" },
          },
          didParseCell: function (data) {
            if (data.column.index === 2 && data.section === "body") {
              const severity = data.cell.text[0];
              const severityColors = {
                Critical: [174, 36, 30],
                High: [255, 64, 64],
                Medium: [255, 165, 0],
                Low: [56, 142, 60],
              };
              if (severityColors[severity]) {
                data.cell.styles.fillColor = severityColors[severity];
                data.cell.styles.textColor = [255, 255, 255];
              }
            }
          },
          margin: { bottom: bottomMargin + 5 },
        });
        currentY = pdf.lastAutoTable.finalY + 20;
      }

      // Start detailed vulnerabilities on new page
      pdf.addPage();
      currentY = 25;

      pdf.setFontSize(18);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Vulnerabilities Details", margin, currentY);
      currentY += 20;

      vulnerabilities.forEach((vuln, index) => {
        // Estimate content height - ADAPTED FOR SAST STRUCTURE
        const linesCount = vuln.lines?.length || 1;
        const estimatedHeight = 120 + Math.min(linesCount * 8, 100);

        if (checkPageBreak(estimatedHeight)) {
          if (currentY <= 25) {
            pdf.setFontSize(18);
            pdf.setTextColor(0, 0, 0);
            pdf.text("Vulnerabilities Details (Continued)", margin, currentY);
            currentY += 20;
          }
        }

        // Vulnerability Title with Severity Badge - ADAPTED FOR SAST
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        const vulnTitle = `${index + 1}. ${vuln.vulnerability_name}`;
        const lineCount = vuln.lines?.length || vuln.Lines?.length || 1;
        pdf.text(`${vulnTitle} (${lineCount})`, margin, currentY);

        const severityColors = {
          Critical: [174, 36, 30],
          High: [255, 64, 64],
          Medium: [255, 165, 0],
          Low: [56, 142, 60],
        };

        const severity = normalizeSeverity(vuln.Impact || vuln.impact);
        const badgeX = pdfWidth - margin - 30;
        pdf.setFillColor(...(severityColors[severity] || [128, 128, 128]));
        pdf.roundedRect(badgeX, currentY - 5, 25, 8, 2, 2, "F");
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text(severity, badgeX + 12.5, currentY, { align: "center" });

        currentY += 20;

        // Description - ADAPTED FOR SAST
        pdf.setFontSize(12);
        pdf.setTextColor(102, 104, 117);
        pdf.text("Description", margin, currentY);
        currentY += 8;

        pdf.setFontSize(9);
        const description = vuln.description || "No description available";
        const splitDescription = pdf.splitTextToSize(
          description,
          availableWidth - 10
        );
        pdf.text(splitDescription, margin, currentY);
        currentY += splitDescription.length * 4 + 15;

        // Affected Locations - ADAPTED FOR SAST STRUCTURE
        if (vuln.lines && vuln.lines.length > 0) {
          checkPageBreak(30);

          pdf.setFontSize(12);
          pdf.setTextColor(102, 104, 117);
          pdf.text(
            `Affected Locations (${vuln.lines.length})`,
            margin,
            currentY
          );
          currentY += 12;

          const locationTableData = vuln.lines.map((line, i) => [
            (i + 1).toString(),
            extractRelevantPath(vuln.File || vuln.file),
            `Line ${line}`,
          ]);

          pdf.autoTable({
            head: [["#", "File Path", "Line"]],
            body: locationTableData,
            startY: currentY,
            styles: {
              fontSize: 8,
              cellPadding: 3,
              lineColor: [221, 221, 221],
              lineWidth: 0.5,
            },
            headStyles: {
              fillColor: [217, 215, 215],
              textColor: 0,
              fontStyle: "bold",
              fontSize: 9,
            },
            columnStyles: {
              0: { cellWidth: 12, halign: "center", fontStyle: "bold" },
              1: { cellWidth: 120, fontSize: 7 },
              2: { cellWidth: 48, fontSize: 7, halign: "center" },
            },
            alternateRowStyles: {
              fillColor: [249, 249, 249],
            },
            margin: {
              left: margin,
              right: margin,
              bottom: bottomMargin + 5,
            },
          });

          currentY = pdf.lastAutoTable.finalY + 15;
        }

        // RECOMMENDED SOLUTIONS - ADAPTED FOR SAST
        if (
          vuln.recommendation &&
          Object.keys(vuln.recommendation).length > 0
        ) {
          let solutionText = "";
          Object.entries(vuln.recommendation).forEach(
            ([language, solution]) => {
              solutionText += `${language}:\n${solution}\n\n`;
            }
          );

          const solutionHeight = calculateTextHeight(
            solutionText,
            availableWidth - 15,
            9
          );
          checkPageBreak(solutionHeight + 20);

          pdf.setFontSize(12);
          pdf.setTextColor(102, 104, 117);
          pdf.text("Recommended Solutions", margin, currentY);
          currentY += 10;

          pdf.setFillColor(254, 242, 242);
          pdf.rect(margin, currentY, availableWidth, solutionHeight, "F");

          pdf.setDrawColor(255, 0, 0);
          pdf.setLineWidth(3);
          pdf.line(margin, currentY, margin, currentY + solutionHeight);

          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 0);
          const splitSolution = pdf.splitTextToSize(
            solutionText,
            availableWidth - 15
          );
          pdf.text(splitSolution, margin + 8, currentY + 8);

          currentY += solutionHeight + 15;
        }

        // REFERENCES - ADAPTED FOR SAST
        if (vuln.references && vuln.references.length > 0) {
          const referenceText = vuln.references.join("\n");
          const referenceHeight = calculateTextHeight(
            referenceText,
            availableWidth - 10,
            9
          );

          checkPageBreak(referenceHeight + 20);

          pdf.setFontSize(12);
          pdf.setTextColor(102, 104, 117);
          pdf.text("References", margin, currentY);
          currentY += 10;

          pdf.setFillColor(240, 244, 255);
          pdf.rect(margin, currentY, availableWidth, referenceHeight, "F");

          pdf.setFontSize(9);
          pdf.setTextColor(25, 118, 210);
          const splitReference = pdf.splitTextToSize(
            referenceText,
            availableWidth - 10
          );
          pdf.text(splitReference, margin + 5, currentY + 8);

          currentY += referenceHeight + 15;
        }

        // Add separator between vulnerabilities
        if (index < vulnerabilities.length - 1) {
          checkPageBreak(15);
          pdf.setDrawColor(202, 202, 202);
          pdf.setLineWidth(0.5);
          pdf.line(margin, currentY, pdfWidth - margin, currentY);
          currentY += 15;
        }
      });

      // Enhanced Footer (same as DAST)
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        pdf.setFillColor(248, 249, 250);
        pdf.rect(0, pdfHeight - bottomMargin, pdfWidth, bottomMargin, "F");

        pdf.setLineWidth(0.3);
        pdf.setDrawColor(200, 200, 200);
        pdf.line(
          0,
          pdfHeight - bottomMargin,
          pdfWidth,
          pdfHeight - bottomMargin
        );

        pdf.setFontSize(9);
        pdf.setTextColor(108, 117, 125);
        pdf.text(
          "This report was automatically generated from the SAST Security Testing Dashboard",
          pdfWidth / 2,
          pdfHeight - 12,
          { align: "center" }
        );

        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pdfWidth - margin,
          pdfHeight - 5,
          {
            align: "right",
          }
        );
      }

      // Enhanced timestamp function with date and time
      function getDetailedTimestamp() {
        const now = new Date();
        const pad = (num) => num.toString().padStart(2, "0");

        const year = now.getFullYear();
        const month = pad(now.getMonth() + 1);
        const day = pad(now.getDate());
        const hours = pad(now.getHours());
        const minutes = pad(now.getMinutes());
        const seconds = pad(now.getSeconds());

        return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      }

      // 🎯 REPLACE THE FINAL SAVE SECTION WITH THIS:
      const timestamp = getDetailedTimestamp();
      const filename = `SAST_Dashboard_${timestamp}.pdf`;

      // Check if File System Access API is supported
      if (window.showSaveFilePicker) {
        try {
          // Show file picker dialog
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "PDF files",
                accept: { "application/pdf": [".pdf"] },
              },
            ],
          });

          // Update button to show saving status
          if (downloadButton) {
            downloadButton.textContent = "Saving...";
          }

          // Create a writable stream to the selected file
          const writable = await fileHandle.createWritable();

          // Write the PDF data
          await writable.write(pdf.output("blob"));

          // Close the file
          await writable.close();

          // Success feedback
          if (downloadButton) {
            downloadButton.textContent = "Saved Successfully!";
            downloadButton.style.backgroundColor = "#4CAF50";

            // Reset button after 2 seconds
            setTimeout(() => {
              downloadButton.textContent = originalText;
              downloadButton.style.backgroundColor = originalBackgroundColor;
            }, 2000);
          }
        } catch (err) {
          if (err.name === "AbortError") {
            // console.log("User cancelled file save");
            return; // Exit without saving
          } else {
            console.error("File save error:", err);
            pdf.save(filename); // Fallback to automatic download
          }
        }
      } else {
        // Fallback for browsers that don't support File System Access API
        pdf.save(filename);
      }
    } catch (error) {
      console.error("Error generating PDF download:", error);
      alert("Error generating PDF download. Please try again.");
    } finally {
      if (downloadButton) {
        downloadButton.textContent = originalText;
        downloadButton.disabled = originalDisabled;
        downloadButton.style.backgroundColor = originalBackgroundColor;
        downloadButton.style.cursor = "pointer";
      }
    }
  };

  // Prepare element for better rendering
  const prepareElementForCapture = async (element) => {
    // Remove scroll constraints
    const originalStyles = new Map();

    // Handle the main element
    originalStyles.set(element, {
      overflow: element.style.overflow,
      maxHeight: element.style.maxHeight,
      height: element.style.height,
    });

    element.style.overflow = "visible";
    element.style.maxHeight = "none";
    element.style.height = "auto";

    // Handle parent containers
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      originalStyles.set(parent, {
        overflow: parent.style.overflow,
        maxHeight: parent.style.maxHeight,
        height: parent.style.height,
      });

      parent.style.overflow = "visible";
      parent.style.maxHeight = "none";
      parent.style.height = "auto";

      parent = parent.parentElement;
    }

    // Handle specific Material-UI components
    const scrollableElements = element.querySelectorAll(
      '.MuiTableContainer-root, .MuiAccordionDetails-root, [style*="overflow"], [style*="maxHeight"]'
    );

    scrollableElements.forEach((el) => {
      originalStyles.set(el, {
        overflow: el.style.overflow,
        maxHeight: el.style.maxHeight,
        overflowY: el.style.overflowY,
      });

      el.style.overflow = "visible";
      el.style.overflowY = "visible";
      el.style.maxHeight = "none";
    });

    // Store for restoration
    element._originalStyles = originalStyles;

    // Wait for layout to settle
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Force re-render of charts if any
    const charts = element.querySelectorAll("[data-chart]");
    charts.forEach((chart) => {
      if (chart._echartsInstance) {
        chart._echartsInstance.resize();
      }
    });

    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  // Restore element styles
  const restoreElement = async (element) => {
    if (element._originalStyles) {
      element._originalStyles.forEach((styles, el) => {
        Object.keys(styles).forEach((prop) => {
          el.style[prop] = styles[prop] || "";
        });
      });
      delete element._originalStyles;
    }
  };

  // Enhanced HTML template
  const createEnhancedHTML = (imgData) => {
    const timestamp = new Date().toLocaleString();
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>SAST Dashboard Report</title>
    <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: 'Roboto', 'Arial', sans-serif;
            background-color: #f5f5f5;
        }
        .header {
            text-align: center;
            margin-bottom: 20px;
            padding: 20px;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .dashboard-image {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
            border-radius: 8px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            background-color: white;
        }
        .footer {
            text-align: center;
            margin-top: 20px;
            padding: 10px;
            color: #666;
            font-size: 12px;
        }
        @media print {
            body { padding: 0; background: white; }
            .header, .footer { break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>SAST Security Dashboard Report</h1>
        <p>Generated on: ${timestamp}</p>
    </div>
    <img class="dashboard-image" src="${imgData}" alt="SAST Dashboard" />
    <div class="footer">
        <p>This report was automatically generated from the SAST Security Testing Dashboard.</p>
    </div>
</body>
</html>`;
  };

  const options = ["All", "Critical", "High", "Medium", "Low"];
  const [value, setValue] = useState("All");

  function normalizeSeverity(sev) {
    if (!sev) return "";
    const val = sev.trim().toUpperCase();
    if (val === "BLOCKER" || val === "CRITICAL") return "Critical";
    if (val === "HIGH") return "High";
    if (val === "MAJOR" || val === "MEDIUM") return "Medium";
    if (val === "MINOR" || val === "LOW") return "Low";
    return val;
  }

  // Simplified useEffect - works with already-grouped data from backend
  useEffect(() => {
    const results = sastScanResult || [];
    const date = scanDate || "";

    setScanResults(results);
    setDateTime(date);

    if (results.length) {
      // Count severities directly from grouped results
      setLowSeverity(
        results.filter(
          (item) =>
            item.Impact === "LOW" ||
            item.impact === "LOW" ||
            item.Severity === "MINOR" ||
            item.Severity === "LOW"
        ).length
      );

      setMediumSeverity(
        results.filter(
          (item) =>
            item.Impact === "MEDIUM" ||
            item.impact === "MEDIUM" ||
            item.Severity === "MEDIUM" ||
            item.Severity === "MAJOR"
        ).length
      );

      setHighSeverity(
        results.filter(
          (item) =>
            item.Impact === "HIGH" ||
            item.impact === "HIGH" ||
            item.Severity === "HIGH"
        ).length
      );

      setCriticalSeverity(
        results.filter(
          (item) =>
            item.Impact === "CRITICAL" ||
            item.impact === "CRITICAL" ||
            item.Severity === "CRITICAL" ||
            item.Severity === "BLOCKER"
        ).length
      );
    } else {
      console.error("No scan results found.");
    }
  }, [sastScanResult]);

  const severityData = [
    { label: "CRITICAL", count: criticalSeverity, color: "#AE241E" },
    { label: "HIGH", count: highSeverity, color: "#FF4040" },
    { label: "MEDIUM", count: mediumSeverity, color: "#FFA500" },
    { label: "LOW", count: lowSeverity, color: "#388E3C" },
  ];

  const SeverityCircle = ({ label, count, color }) => (
    <Box
      sx={{
        width: 170,
        height: 170,
        borderRadius: "50%",
        backgroundColor: "white",
        position: "relative",
        mx: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `12px 0 0 0 ${color}`,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            fontSize: 26,
            color: count === 0 ? "#757575" : color,
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: 14,
            color: "black",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  // Bar Chart Data
  const barChartOptions = {
    tooltip: { trigger: "axis" },
    xAxis: { type: "category", data: ["Critical", "High", "Medium", "Low"] },
    yAxis: { type: "value" },
    series: [
      {
        name: "Issues",
        type: "bar",
        data: [criticalSeverity, highSeverity, mediumSeverity, lowSeverity],
        itemStyle: {
          color: function (params) {
            const colors = ["#c00000", "#ff0000", "#ffc000", "#39b549"];
            return colors[params.dataIndex];
          },
        },
      },
    ],
  };

  const extractRelevantPath = (fullPath) => {
    if (!fullPath) return "";
    const normalizedPath = fullPath.replace(/\\/g, "/");
    const match = normalizedPath.match(/(?:giturl|[^/]+_\d+)\/(.+)/);
    return match ? match[1] : fullPath;
  };

  // Formatted results for download
  const formattedScanResult = scanResults.map((scanResult) => {
    const flattenScanResults = {};
    Object.entries(scanResult).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        flattenScanResults[key] = flattenArray(value);
      } else if (typeof value === "object" && !!value) {
        flattenScanResults[key] = flattenObject(value);
      } else {
        flattenScanResults[key] = value;
      }
    });
    return flattenScanResults;
  });

  const tableDataConverter = (scanResults) => {
    const headers = Object.keys(scanResults[0]);
    const values = scanResults.map((eachScanResult) => {
      return Object.values(eachScanResult);
    });
    return { headers, values };
  };

  const handleNavigate = async () => {
    navigate("/dashboard/security-testing/dast-phase");
  };

  // Highlight search terms
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark
          key={i}
          style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const formatDescription = (text, searchTerm) => {
    if (!text) return null;

    const lines = text.split(/\n|(?=\s*\*)/g);

    return lines.map((line, i) => {
      const trimmed = line.trim();

      const parts = [];
      const regex = /(["'])(?:(?=(\\?))\2.)*?\1/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(trimmed)) !== null) {
        const before = trimmed.slice(lastIndex, match.index);
        if (before) parts.push(highlightMatch(before, searchTerm));

        const quoted = match[0].slice(1, -1);
        parts.push(
          <strong key={`quoted-${i}-${match.index}`}>
            {highlightMatch(quoted, searchTerm)}
          </strong>
        );

        lastIndex = regex.lastIndex;
      }

      const after = trimmed.slice(lastIndex);
      if (after) parts.push(highlightMatch(after, searchTerm));

      if (trimmed.startsWith("*")) {
        return (
          <li key={i} style={{ marginBottom: "6px", marginLeft: "20px" }}>
            {parts}
          </li>
        );
      }

      return (
        <p key={i} style={{ margin: "6px 0" }}>
          {parts}
        </p>
      );
    });
  };

  return (
    <>
      <CommonAccordion
        summary={"Dashboard"}
        details={
          <Box ref={dashboardRef} data-dashboard-ref="true" sx={{ padding: 2 }}>
            {/* Header with updated download buttons */}
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h5" fontWeight="bold" fontSize={20}>
                SAST Scan Dashboard
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Tooltip title="Download as HTML file" placement="top" arrow>
                  <Button
                    variant="contained"
                    disabled={isDownloading}
                    sx={{
                      backgroundColor: isDownloading ? "#A5A5A5" : "#49A3FF",
                      fontSize: 14,
                      width: 120,
                      height: 40,
                      textTransform: "none",
                    }}
                    startIcon={<ExportIcon style={{ width: 15, height: 15 }} />}
                    onClick={handleDownloadHTML}
                  >
                    {isDownloading ? "Processing..." : "HTML"}
                  </Button>
                </Tooltip>

                <Tooltip title="Download as PDF file" placement="top" arrow>
                  <Button
                    variant="contained"
                    disabled={isDownloadingPDF}
                    sx={{
                      backgroundColor: isDownloadingPDF ? "#A5A5A5" : "#49A3FF",
                      fontSize: 14,
                      width: 120,
                      height: 40,
                      textTransform: "none",
                    }}
                    startIcon={<ExportIcon style={{ width: 15, height: 15 }} />}
                    onClick={handleDownloadPDF}
                  >
                    {isDownloadingPDF ? "Processing..." : "PDF"}
                  </Button>
                </Tooltip>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#49A3FF",
                    fontSize: 14,
                    width: 140,
                    height: 40,
                    textTransform: "none",
                  }}
                  startIcon={<ExportIcon style={{ width: 15, height: 15 }} />}
                  onClick={() => setOpenDownload(true)}
                >
                  Full Report
                </Button>
              </Box>
            </Box>

            {/* Scan Information Section */}
            <Box
              sx={{ backgroundColor: "#F1F8FF", p: 3, mb: 3, borderRadius: 4 }}
            >
              <Typography fontSize={14} mb={1}>
                Scan Information
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography fontSize={14}>
                  Last Scan completed on {dateTime}
                </Typography>
                <Typography fontSize={14}>
                  Total vulnerabilities: {scanResults.length}
                </Typography>
              </Box>
            </Box>

            {/* Summary Cards */}
            <Box p={3} sx={{ backgroundColor: "#f6f3f2" }}>
              <Grid container justifyContent="space-around" spacing={3} mt={3}>
                {severityData.map((item, index) => (
                  <Grid
                    item
                    key={index}
                    xs={12}
                    sm={6}
                    md={3}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SeverityCircle
                      label={item.label}
                      count={item.count}
                      color={item.color}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Bar Chart */}
            <Card sx={{ mt: 6 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold">
                  Vulnerability Count By Severity
                </Typography>
                <ReactECharts
                  option={barChartOptions}
                  style={{ height: "300px" }}
                />
              </CardContent>
            </Card>

            {/* Source Code Summary Table */}
            <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  mt={4}
                  mb={3}
                  sx={{ fontSize: "24px", fontWeight: "500" }}
                >
                  Source Code Summary
                </Typography>

                <Box sx={{ position: "relative", mt: 2 }}>
                  <Autocomplete
                    value={languageFilter}
                    onChange={(event, newValue) =>
                      setLanguageFilter(newValue || "All")
                    }
                    inputValue={searchInput}
                    onInputChange={(event, newInputValue) =>
                      setSearchInput(newInputValue)
                    }
                    options={[
                      "All",
                      ...(codeSummary?.languages?.map(
                        (lang) => lang.language
                      ) || []),
                    ]}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      return options.filter((option) =>
                        option.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Filter by language..."
                        variant="outlined"
                        size="small"
                        sx={{
                          width: 220,
                          backgroundColor: "#f0f2f5",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f0f2f5",
                            borderRadius: "8px",
                            fontSize: "14px",
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "1px solid #4285f4",
                            },
                          },
                          "& .MuiInputBase-input": {
                            padding: "8px 12px",
                            fontSize: "14px",
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <SearchIcon
                              sx={{ color: "#666", mr: 1, fontSize: "18px" }}
                            />
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          fontSize: "14px",
                          padding: "8px 12px",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#E3F2FD",
                          },
                        }}
                      >
                        {option === "All" ? "All Languages" : option}
                      </Box>
                    )}
                    popupIcon={null}
                    clearIcon={
                      searchInput ? (
                        <ClearIcon sx={{ fontSize: "16px" }} />
                      ) : null
                    }
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": {
                        display: "none",
                      },
                    }}
                  />
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                sx={{ minHeight: "1vh", maxHeight: "70vh", overflowY: "auto" }}
              >
                <Table stickyHeader sx={{ border: "1px solid #ddd" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#4285f4",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        LANGUAGE
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#4285f4",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        FILES
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#4285f4",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        BLANK
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#4285f4",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        COMMENT
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#4285f4",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        CODE
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {!codeSummary?.languages ||
                    codeSummary.languages.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ fontSize: "16px", padding: "20px" }}
                        >
                          No language data available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      codeSummary.languages
                        .filter(
                          (lang) =>
                            languageFilter === "All" ||
                            lang.language === languageFilter
                        )
                        .map((lang, index) => (
                          <TableRow
                            key={lang.language}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#FFFFFF" : "#F9FAFB",
                            }}
                          >
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#000",
                              }}
                            >
                              {lang.language}
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                              }}
                            >
                              {lang.files.toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                              }}
                            >
                              {lang.blanks.toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                              }}
                            >
                              {lang.comments.toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#4285f4",
                              }}
                            >
                              {(
                                lang.lines -
                                lang.comments -
                                lang.blanks
                              ).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary Statistics */}
              {codeSummary?.summary && (
                <Box sx={{ mt: 2, display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#4285f4",
                      }}
                    />
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      Total Languages: {codeSummary.languages.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#34a853",
                      }}
                    />
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      Total Lines:{" "}
                      {codeSummary.summary.totalLines.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#ff9800",
                      }}
                    />
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      Total Code Lines:{" "}
                      {(
                        codeSummary.summary.totalLines -
                        codeSummary.summary.totalComments -
                        codeSummary.summary.totalBlanks
                      ).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Vulnerabilities by Name and Severity Table */}
            <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  mt={4}
                  mb={3}
                  sx={{ fontSize: "24px", fontWeight: "500" }}
                >
                  Vulnerabilities by Name and Severity
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f0f2f5",
                    borderRadius: "8px",
                    width: 80,
                    height: 35,
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    mt: 2,
                  }}
                >
                  <Select
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    displayEmpty
                    variant="standard"
                    disableUnderline
                    sx={{
                      fontSize: "14px",
                      backgroundColor: "transparent",
                      flexGrow: 1,
                    }}
                  >
                    {options.map((opt) => (
                      <MenuItem
                        key={opt}
                        value={opt}
                        sx={{
                          fontSize: "16px",
                          paddingY: "10px",
                          "&.Mui-selected": {
                            backgroundColor: "transparent",
                          },
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              <TableContainer
                component={Paper}
                sx={{ minHeight: "1vh", maxHeight: "70vh", overflowY: "auto" }}
              >
                <Table stickyHeader sx={{ border: "1px solid #ddd" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#0085DE",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "15px",
                        }}
                      >
                        S.No
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#0085DE",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "15px",
                        }}
                      >
                        Vulnerability Name
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#0085DE",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "15px",
                        }}
                      >
                        Severity
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody
                    sx={{
                      minHeight: "1vh",
                      maxHeight: "10vh",
                      overflowY: "auto",
                    }}
                  >
                    {(() => {
                      // Filter directly from scanResults since it's already grouped
                      const filteredVulns = scanResults.filter(
                        (vuln) =>
                          value === "All" ||
                          normalizeSeverity(vuln.Impact || vuln.impact) ===
                            value
                      );

                      if (filteredVulns.length === 0) {
                        return (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ fontSize: "16px", padding: "20px" }}
                            >
                              No vulnerabilities found for selected severity.
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return filteredVulns.map((vuln, index) => {
                        // Get the enhanced vulnerability name from LLM response
                        const enhancedVulnName = vuln.vulnerability_name;
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "#FFFFFF" : "#F0F9FF",
                            }}
                          >
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                              }}
                            >
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {enhancedVulnName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                fontSize: "14px",
                              }}
                            >
                              <Chip
                                label={normalizeSeverity(
                                  vuln.Impact || vuln.impact
                                )}
                                size="small"
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  backgroundColor:
                                    normalizeSeverity(
                                      vuln.Impact || vuln.impact
                                    ) === "Critical"
                                      ? "#c00000"
                                      : normalizeSeverity(
                                          vuln.Impact || vuln.impact
                                        ) === "High"
                                      ? "#ff0000"
                                      : normalizeSeverity(
                                          vuln.Impact || vuln.impact
                                        ) === "Medium"
                                      ? "#ffc000"
                                      : normalizeSeverity(
                                          vuln.Impact || vuln.impact
                                        ) === "Low"
                                      ? "#39b549"
                                      : "#9E9E9E",
                                  borderRadius: "8px",
                                  width: "86px",
                                  height: "32px",
                                  color: "#fff",
                                  textTransform: "capitalize",
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Enhanced Vulnerabilities Details Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 3,
                mt: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
                    Vulnerabilities Details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#f0f2f5",
                    borderRadius: "8px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", mr: 1 }}>
                    Sort By:
                  </Typography>
                  <Select
                    value={sortSeverity}
                    onChange={(e) => setSortSeverity(e.target.value)}
                    variant="standard"
                    disableUnderline
                    sx={{
                      fontSize: "14px",
                      backgroundColor: "transparent",
                      flexGrow: 1,
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </Box>
              </Box>

              <Box sx={{ maxHeight: 600, overflowY: "auto", pr: 1 }}>
                {(() => {
                  // Filter directly from scanResults since it's already grouped
                  const filteredResults = scanResults.filter((item) => {
                    if (sortSeverity === "All") return true;
                    return (
                      normalizeSeverity(item.Impact || item.impact) ===
                      sortSeverity
                    );
                  });

                  if (filteredResults.length === 0) {
                    return (
                      <Typography
                        sx={{
                          textAlign: "center",
                          mt: 4,
                          fontSize: "16px",
                          color: "#888",
                        }}
                      >
                        No vulnerabilities found for the selected severity.
                      </Typography>
                    );
                  }

                  return filteredResults.map((item, index) => {
                    return (
                      <Box
                        key={index}
                        height="100%"
                        borderRadius="8px"
                        sx={{
                          backgroundColor: "#B5B5B50F",
                          p: 3,
                          boxShadow: 1,
                          mt: 2,
                          mb: 2,
                        }}
                      >
                        {/* Header with Title and Severity */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "22px", fontWeight: "700" }}
                            >
                              {index + 1}. {item.vulnerability_name}
                              <Tooltip title="Count">
                                ({item.lines?.length || item.Lines?.length || 1}
                                )
                              </Tooltip>
                            </Typography>
                          </Box>
                          <Tooltip title="Severity">
                            <Box
                              sx={{
                                backgroundColor:
                                  (item.Impact || item.impact) === "CRITICAL"
                                    ? "#c00000"
                                    : (item.Impact || item.impact) === "HIGH"
                                    ? "#ff0000"
                                    : (item.Impact || item.impact) === "MEDIUM"
                                    ? "#ffc000"
                                    : (item.Impact || item.impact) === "LOW"
                                    ? "#39b549"
                                    : "#F0F0F0",
                                borderRadius: "4px",
                                width: 100,
                                height: 35,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#fff",
                                  fontSize: "14px",
                                }}
                              >
                                {normalizeSeverity(item.Impact || item.impact)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Enhanced Description from LLM */}
                        {item.description && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              mt: 3,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#666875",
                              }}
                            >
                              Description
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color: "#666875",
                                mt: 1,
                              }}
                            >
                              {formatDescription(item.description, searchTerm)}
                            </Typography>
                          </Box>
                        )}

                        {/* Affected Locations */}
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#666875",
                            }}
                          >
                            Affected Locations:
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {item.lines?.map((line, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  p: 0.5,
                                  backgroundColor: "#f9f9f9",
                                  borderRadius: "4px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "16px",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "normal",
                                    color: "#666875",
                                    fontWeight: "500",
                                  }}
                                >
                                  {extractRelevantPath(item.File || item.file)}{" "}
                                  : {line}
                                </Typography>
                              </Box>
                            )) || (
                              <Box
                                sx={{
                                  p: 0.5,
                                  backgroundColor: "#f9f9f9",
                                  borderRadius: "4px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "16px",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "normal",
                                    color: "#666875",
                                    fontWeight: "500",
                                  }}
                                >
                                  {extractRelevantPath(item.File || item.file)}{" "}
                                  : Line {item.Line || item.original_line}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Enhanced Recommendations from LLM */}
                        {item.recommendation &&
                          Object.keys(item.recommendation).length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                mt: 3,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  color: "#666875",
                                }}
                              >
                                Recommended Solutions
                              </Typography>
                              <Box
                                component="ul"
                                p={1}
                                pl={2}
                                sx={{
                                  backgroundColor: "#FEF2F2",
                                  borderRadius: "8px",
                                  listStyleType: "disc",
                                  listStylePosition: "outside",
                                  borderLeft: "8px solid #FF0000",
                                  lineHeight: 1.7,
                                  paddingLeft: "24px",
                                }}
                              >
                                {Object.entries(item.recommendation).map(
                                  ([language, solution]) => (
                                    <Box key={language} sx={{ mb: 2 }}>
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          color: "#1976d2",
                                          mb: 0.5,
                                        }}
                                      >
                                        {language}:
                                      </Typography>
                                      <Typography
                                        component="div"
                                        sx={{
                                          whiteSpace: "pre-line",
                                          margin: 0,
                                          fontSize: "15px",
                                          color: "#2d3748",
                                          wordWrap: "break-word",
                                        }}
                                      >
                                        {solution}
                                      </Typography>
                                    </Box>
                                  )
                                )}
                              </Box>
                            </Box>
                          )}

                        {/* Enhanced References from LLM */}
                        {item.references && item.references.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              mt: 3,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#333",
                                mb: 1,
                              }}
                            >
                              References
                            </Typography>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 1.5,
                                backgroundColor: "#F0F4FF",
                                borderRadius: "10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                fontSize: "16px",
                                color: "#1976d2",
                                wordBreak: "break-word",
                              }}
                            >
                              {item.references.map((ref, idx) => (
                                <Typography
                                  key={idx}
                                  component="a"
                                  href={ref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: "15px",
                                    color: "#1976d2",
                                    textDecoration: "none",
                                    display: "block",
                                    mb: 0.5,
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {ref}
                                </Typography>
                              ))}
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          </Box>
        }
        buttons={[
          {
            label: "Next",
            startIcon: <GenerateButton />,
            onClick: handleNavigate,
            variant: "contained",
            sx: { textTransform: "none", color: "white", float: "left" },
          },
        ]}
        type={"output"}
        fullScreenMode={true}
        defaultExpanded={true}
      />
      {/* CommonDownload */}
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceName="SAST Scan Report"
        serviceResponse={scanResults} // Use original scanResults instead of flattened
        runType={runType} // Pass the runType prop
        wordData={[
          {
            heading: "SAST Scan Report",
            type: "table",
            data: { ...tableDataConverter(formattedScanResult) },
          },
        ]}
        documents={["xlsx", "csv"]}
      />
    </>
  );
};

export default SASTScanDashboard;
